import { Dayjs } from "dayjs";
import DecimalType from "decimal.js";
/**
 * Given a type,
 * recursively transforms it into the type of the result of doing `JSON.parse(JSON.stringify(valWithType))`.
 * Does NOT handle toJSON (except for special cases, like Dayjs and Date),
 * assumes default JSON.stringify handling,
 * only works well for stuff like Prisma DTOs.
 */
export type SerialType<T> =
T extends (number | string | null | undefined)
  ? T
  : Date extends T
    ? string
    : Dayjs extends T
      ? string
      : bigint extends T
        ? number
        : DecimalType extends T
          ? string
          : T extends (infer E)[]
            ? SerialType<E>[]
            : T extends object
              ? {
                [k in keyof T]: SerialType<T[k]>;
              }
              : T;

/** Casts the given value to its serial type.
 * It does NOT convert the input, it just returns the input as-is.
 * Requires you to explicitly provide the type to increase type safety.
 * This is useful for inputs that have a Serial type but can actually
 * also accept non-serial types and serialise them as needed (e.g. when
 * inserting into a JSONB column with Prisma). */
export function castToSerialType<T = "No type parameter was supplied">(valWithType: NoInfer<T>): SerialType<NoInfer<T>> {
  return valWithType as SerialType<T>;
}

/**
 * Serializes complex objects to plain JSON-serializable types.
 * Useful for Next.js server-to-client component communication.
 * Avoids "Only plain objects can be passed to Client Components" warnings
 *
 * Type transformations:
 * - Decimal objects → strings
 * - Date objects → ISO strings
 * - BigInt → numbers
 *
 * Client component can convert back when needed
 */
export function serialize<T>(valWithType: T): SerialType<T> {
  return JSON.parse(JSON.stringify(valWithType));
}
